import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, FolderSync, Plus, Save, Trash2 } from "lucide-react";
import type { Agent } from "@/types";
import { SectionCard } from "@/components/SectionCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useWorkspaceSync } from "@/api/queries/workspaceSync";
import {
  useAddSyncDevice,
  useDisableSync,
  useProvisionSync,
  useRemoveSyncDevice,
  useSetSyncFolderType,
  useSetSyncIgnore,
} from "@/api/mutations/workspaceSync";

// #229: Workspace-Live-Sync auf der Agent-Detail-Seite. Zeigt die Device-ID des Agents +
// Relay-Adresse (die der User in SEIN Syncthing einträgt), die freigeschalteten User-Geräte
// mit Live-Verbindung, und erlaubt Geräte hinzuzufügen/entfernen + die Richtung zu setzen.
export function WorkspaceSyncSection({ agent }: { agent: Agent }) {
  const { data, isLoading } = useWorkspaceSync(agent.id);
  const addDevice = useAddSyncDevice(agent.id);
  const removeDevice = useRemoveSyncDevice(agent.id);
  const setFolderType = useSetSyncFolderType(agent.id);
  const disableSync = useDisableSync(agent.id);
  const provisionSync = useProvisionSync(agent.id);
  const setIgnore = useSetSyncIgnore(agent.id);

  const [deviceId, setDeviceId] = useState("");
  const [label, setLabel] = useState("");

  // Ignore-Patterns als Text (eine pro Zeile), aus dem Status hydriert (dirty-Tracking).
  const [ignoreText, setIgnoreText] = useState("");
  const [ignoreDirty, setIgnoreDirty] = useState(false);
  useEffect(() => {
    if (data?.ignorePatterns && !ignoreDirty) setIgnoreText(data.ignorePatterns.join("\n"));
  }, [data?.ignorePatterns, ignoreDirty]);

  const copy = (text: string, what: string) => {
    void navigator.clipboard.writeText(text).then(
      () => toast.success(`${what} kopiert`),
      () => toast.error("Kopieren fehlgeschlagen"),
    );
  };

  function add() {
    if (!deviceId.trim()) return;
    addDevice.mutate(
      { deviceId: deviceId.trim(), label: label.trim() || "device" },
      {
        onSuccess: () => {
          setDeviceId("");
          setLabel("");
          toast.success("Gerät freigeschaltet");
        },
        onError: (err) => toast.error(`Freischalten fehlgeschlagen: ${(err as Error).message}`),
      },
    );
  }

  return (
    <SectionCard
      icon={<FolderSync className="h-[18px] w-[18px]" />}
      title="Workspace-Sync"
      description="Lokalen Ordner ↔ Agent-/workspace live syncen (Syncthing über self-hosted Relay)"
      action={
        data?.available && data.userDevices.length > 0 ? (
          <Button
            variant="outline"
            size="sm"
            disabled={disableSync.isPending}
            onClick={() =>
              disableSync.mutate(undefined, {
                onSuccess: () => toast.success("Sync deaktiviert"),
                onError: (err) => toast.error(`Deaktivieren fehlgeschlagen: ${(err as Error).message}`),
              })
            }
            data-testid="sync-disable"
          >
            Sync aus
          </Button>
        ) : undefined
      }
    >
      {isLoading ? (
        <p className="text-[13px] text-text3">Lade …</p>
      ) : data && !data.relayConfigured ? (
        <div className="space-y-2" data-testid="sync-relay-unconfigured">
          <p className="text-[13px] text-text3">
            Workspace-Sync ist auf diesem System nicht eingerichtet. Der Betreiber muss zuerst die
            Relay-Adresse in der Server-Konfiguration setzen (ENV <code className="mono">
            SYNCTHING_RELAY_PUBLIC_HOST</code>), dann lässt sich Sync pro Agent aktivieren.
          </p>
        </div>
      ) : !data?.available ? (
        <div className="space-y-3" data-testid="sync-unavailable">
          <p className="text-[13px] text-text3">
            Dieser Agent hat noch keine Sync-Identität. Einmalig provisionieren (mintet ein
            Geräte-Cert; startet den Brain neu, damit die Identität ankommt), dann ist Workspace-Sync
            verfügbar.
          </p>
          <Button
            size="sm"
            disabled={provisionSync.isPending}
            onClick={() =>
              provisionSync.mutate(undefined, {
                onSuccess: () => toast.success("Sync-Identität provisioniert — Agent wird neu gestartet"),
                onError: (err) => toast.error(`Provisionieren fehlgeschlagen: ${(err as Error).message}`),
              })
            }
            data-testid="sync-provision"
          >
            {provisionSync.isPending ? "Provisioniere …" : "Sync für diesen Agent aktivieren"}
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Identität + Relay (vom User in sein Syncthing einzutragen) */}
          <div className="space-y-2">
            <CopyRow label="Agent-Device-ID" value={data.deviceId ?? ""} onCopy={copy} testId="sync-device-id" />
            <CopyRow label="Relay-Adresse" value={data.relayUrl} onCopy={copy} testId="sync-relay" />
            <CopyRow label="Folder-ID" value={data.folderId} onCopy={copy} testId="sync-folder-id" />
            <div className="flex items-center gap-2 text-[12px]">
              <span className={data.online ? "text-ok-text" : "text-text3"}>
                ● Daemon {data.online ? "läuft" : "gestoppt"}
              </span>
              {data.online && (
                <span className="text-text3">· {data.connectedDeviceIds.length} Gerät(e) verbunden</span>
              )}
            </div>
          </div>

          {/* Richtung */}
          <div className="flex items-center gap-2">
            <label className="text-[12px] font-semibold text-text2">Richtung</label>
            <select
              className="rounded-md border border-border bg-surface px-2 py-1 text-[13px]"
              value={data.folderType}
              disabled={setFolderType.isPending}
              onChange={(e) => {
                const ft = e.target.value as "sendreceive" | "receiveonly" | "sendonly";
                setFolderType.mutate(ft, {
                  onSuccess: () => toast.success("Richtung gesetzt"),
                  onError: (err) => toast.error(`Richtung fehlgeschlagen: ${(err as Error).message}`),
                });
              }}
              data-testid="sync-folder-type"
            >
              <option value="sendreceive">beide (bidirektional)</option>
              <option value="receiveonly">nur empfangen (User liefert, Agent ändert nicht)</option>
              <option value="sendonly">nur senden (Agent liefert, User ändert nicht)</option>
            </select>
          </div>

          {/* Freigeschaltete User-Geräte */}
          <div>
            <div className="mb-1.5 text-[12px] font-semibold text-text2">Freigeschaltete Geräte</div>
            {data.userDevices.length === 0 ? (
              <p className="text-[12px] text-text3">
                Kein Gerät freigeschaltet — Sync ist aus. Trage unten die Device-ID deiner Syncthing-
                Installation ein, um den lokalen Ordner anzubinden.
              </p>
            ) : (
              <ul className="space-y-1.5" data-testid="sync-device-list">
                {data.userDevices.map((d) => {
                  const connected = data.connectedDeviceIds.includes(d.deviceId);
                  return (
                    <li key={d.deviceId} className="flex items-center gap-2 rounded-md border border-border px-2.5 py-1.5">
                      <span className={connected ? "text-ok-text" : "text-text3"} title={connected ? "verbunden" : "nicht verbunden"}>●</span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-[13px] font-medium text-text">{d.label}</span>
                        <span className="mono block truncate text-[11px] text-text3">{d.deviceId}</span>
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={removeDevice.isPending}
                        onClick={() =>
                          removeDevice.mutate(d.deviceId, {
                            onSuccess: () => toast.success("Gerät entfernt"),
                            onError: (err) => toast.error(`Entfernen fehlgeschlagen: ${(err as Error).message}`),
                          })
                        }
                        data-testid={`sync-remove-${d.deviceId}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Gerät hinzufügen */}
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <label className="mb-1 block text-[12px] font-semibold text-text2">Device-ID des User-Geräts</label>
              <Input
                value={deviceId}
                onChange={(e) => setDeviceId(e.target.value)}
                placeholder="XXXXXXX-XXXXXXX-…"
                className="mono text-[12px]"
                data-testid="sync-add-device-id"
              />
            </div>
            <div className="w-40">
              <label className="mb-1 block text-[12px] font-semibold text-text2">Label</label>
              <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Laptop" data-testid="sync-add-label" />
            </div>
            <Button size="sm" onClick={add} disabled={!deviceId.trim() || addDevice.isPending} data-testid="sync-add-device">
              {addDevice.isPending ? <Plus className="h-4 w-4 animate-pulse" /> : <Plus className="h-4 w-4" />}
              Freischalten
            </Button>
          </div>

          {/* Ignore-Patterns (.stignore) */}
          <div>
            <div className="mb-1 flex items-center justify-between">
              <label className="text-[12px] font-semibold text-text2">Ignorieren (.stignore) — eine Regel pro Zeile</label>
              <Button
                size="sm"
                variant="outline"
                disabled={!ignoreDirty || setIgnore.isPending}
                onClick={() =>
                  setIgnore.mutate(
                    ignoreText.split("\n").map((l) => l.trim()).filter(Boolean),
                    {
                      onSuccess: () => { setIgnoreDirty(false); toast.success("Ignore-Regeln gespeichert"); },
                      onError: (err) => toast.error(`Speichern fehlgeschlagen: ${(err as Error).message}`),
                    },
                  )
                }
                data-testid="sync-ignore-save"
              >
                <Save className="h-4 w-4" /> Speichern
              </Button>
            </div>
            <Textarea
              value={ignoreText}
              onChange={(e) => { setIgnoreText(e.target.value); setIgnoreDirty(true); }}
              rows={6}
              className="mono text-[12px]"
              placeholder={"node_modules\n.venv\n__pycache__"}
              data-testid="sync-ignore"
            />
            <p className="mt-1 text-[11px] text-text3">
              Was NICHT gesynct wird (von Tools installierte/regenerierbare Sachen). <span className="mono">.git</span> und{" "}
              <span className="mono">attachments</span> werden immer ignoriert (Schutz vor GitSync-Kollision).
            </p>
          </div>
        </div>
      )}
    </SectionCard>
  );
}

function CopyRow({
  label, value, onCopy, testId,
}: {
  label: string;
  value: string;
  onCopy: (text: string, what: string) => void;
  testId: string;
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="w-32 shrink-0 text-[12px] font-semibold text-text2">{label}</span>
      <span className="mono min-w-0 flex-1 truncate text-[12px] text-text" data-testid={testId} title={value}>
        {value || "—"}
      </span>
      {value && (
        <Button variant="ghost" size="sm" onClick={() => onCopy(value, label)}>
          <Copy className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}
